import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import useHttpHook from "../../Hooks/HttpHook";
import { UpdatePromoCodeApi } from "../../utils/ApiConstant";
import { PromocodeLanguage } from "../../utils/Languages";
import MainComponentWrapper from "../Common/MainComponentWrapper";
import InfiniteProgressBar from "../Common/InfiniteProgressBar";
import ModalWrapper from "../Common/Portals/ModalWrapper";
import Input from "../UiElement/FormElement/Input";
import Select from "react-select";
import { toast } from "react-toastify";
import { promoCodeSelectStyles } from "./promoCodeSelectStyles";
import PromoCodeDateField from "./PromoCodeDateField";
import { showErrorToast } from "../../utils/notify";

const EditPromocodeModel = ({
  closeEditModel,
  promocode,
  refetch,
  eventList,
}) => {
  const [loading, sendRequest, error] = useHttpHook();
  const [selectetEvents, setSelectedEvents] = useState([]);
  const [validationErr, setValidationErr] = useState("");
  const [options, setOptions] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  useEffect(() => {
    if (error) {
      if (toast.isActive(error) === false) {
        showErrorToast(error);
        if (error === "You are unAuthrised") {
          window.location.href = `${process.env.REACT_APP_MERCH_URL}/login`;
        }
      }
    }
  }, [error]);

  useEffect(() => {
    const eventKey = (event) => String(event?._id ?? event ?? "");
    const selectedIds = new Set((promocode?.events || []).map(eventKey));
    const isPastEvent = (event) => {
      const endDate = event?.eventTiming?.endDate;
      return Boolean(endDate) && new Date(endDate) < new Date();
    };
    const visibleEvents = (eventList || []).filter(
      (event) => !isPastEvent(event) || selectedIds.has(eventKey(event))
    );
    const mapped = visibleEvents.map((event) => ({
      value: event._id,
      label: event.title,
    }));
    setOptions(mapped);
    setSelectedEvents(
      mapped.filter((option) => selectedIds.has(eventKey(option.value)))
    );
  }, [promocode, eventList]);

  useEffect(() => {
    reset({
      type: promocode.type,
      promocode: promocode.promocode,
      discount: promocode.discount,
      expiryDate: new Date(promocode.expiryDate).toISOString().split("T")[0],
      minPurchase: promocode.minPurchase,
    });
  }, [promocode, reset]);

  const onSubmit = async (data) => {
    if (selectetEvents.length < 1) {
      toast.error("Please select at least one event");
      return;
    }

    if (data.type === "fixed" && +data.discount >= +data.minPurchase) {
      setValidationErr("Discount should be less than minimum purchase amount");
      setTimeout(() => {
        setValidationErr("");
      }, 3000);
      return;
    }

    const obj = {
      ...data,
      events: selectetEvents.map((event) => event.value),
    };

    const res = await sendRequest(`${UpdatePromoCodeApi}/${promocode.promocode}`, {
      method: "PUT",
      data: obj,
    });

    if (res?.status === 1) {
      await refetch();
      closeEditModel();
    }
  };

  const fieldProps = {
    CustomFormGroupCls: "event-promo-code-field",
    InputClassName: "event-promo-code-input",
    LabelCls: "event-promo-code-label",
    WrapperClass: "",
  };

  return (
    <MainComponentWrapper>
      {loading ? (
        <InfiniteProgressBar />
      ) : (
        <ModalWrapper
          title={PromocodeLanguage.EditPromocodeForm.FormTitle}
          submitText={PromocodeLanguage.EditPromocodeForm.SubmitText}
          CloseModalHandler={closeEditModel}
          formId="edit-promocode"
          dialogClassName="event-promo-code-modal-dialog"
          contentClassName="event-promo-code-modal"
          headerClassName="event-promo-code-modal-header"
          bodyClassName="event-promo-code-modal-body"
          footerClassName="event-promo-code-modal-footer"
          closeClass="event-promo-code-modal-btn-close"
          submitClass="event-promo-code-modal-btn-save"
        >
          <form
            className="event-promo-code-form"
            id="edit-promocode"
            onSubmit={handleSubmit(onSubmit)}
          >
            {validationErr && (
              <div className="event-promo-code-alert">{validationErr}</div>
            )}

            <div className="event-promo-code-field">
              <label className="event-promo-code-label" htmlFor="promo-events-edit-select">
                Select Events*
              </label>
              <Select
                inputId="promo-events-edit-select"
                value={selectetEvents}
                onChange={(value) => setSelectedEvents(value || [])}
                options={options}
                isMulti
                isSearchable
                isClearable
                placeholder="Select Events"
                name="events"
                classNamePrefix="event-promo-select"
                styles={promoCodeSelectStyles}
              />
            </div>

            <Input
              {...fieldProps}
              ValidationArr={[{ Type: "required" }]}
              element="number"
              FieldName="discount"
              Register={register}
              Errors={errors}
              placeholder={
                PromocodeLanguage.AddPromocodeForm.FormLabal.discount.placeHolder
              }
              Label={PromocodeLanguage.AddPromocodeForm.FormLabal.discount.label}
            />

            <Input
              {...fieldProps}
              ValidationArr={[{ Type: "required" }]}
              element="number"
              FieldName="minPurchase"
              Register={register}
              Errors={errors}
              placeholder={
                PromocodeLanguage.AddPromocodeForm.FormLabal.minPurchase.placeHolder
              }
              Label={
                PromocodeLanguage.AddPromocodeForm.FormLabal.minPurchase.label
              }
            />

            <PromoCodeDateField
              register={register}
              errors={errors}
              label={
                PromocodeLanguage.AddPromocodeForm.FormLabal.expiryDate.label
              }
            />

            <div className="event-promo-code-field">
              <span className="event-promo-code-type-label">
                {PromocodeLanguage.AddPromocodeForm.FormLabal.type.label}
              </span>
              <div className="event-promo-code-radios">
                <label className="event-promo-code-radio">
                  <input
                    type="radio"
                    value="percentage"
                    {...register("type")}
                  />
                  Percentage
                </label>
                <label className="event-promo-code-radio">
                  <input type="radio" value="fixed" {...register("type")} />
                  Fixed Amount
                </label>
              </div>
            </div>
          </form>
        </ModalWrapper>
      )}
    </MainComponentWrapper>
  );
};

export default EditPromocodeModel;
